import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, X, Minus, ChevronDown, ChevronUp, Sparkles, Plus, Eye, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import EditableField from '../common/EditableField';
import LineItemTable from './LineItemTable';
import {
  attributeAiStatus, attributeDisplayStatus, attributeFinding, attributeHumanStatus, notCompletedCount,
  reviewerLabel, sampleDisplayStatus, sampleHasReviewerOverride,
} from '../../features/control-testing/report/status.ts';
import {
  aiExtraColumns, loadColumnChoices, lookupField, pivotSamples, planFieldColumns, resolveColumns, saveColumnChoice, toTitle,
} from '../../features/control-testing/report/fields.ts';

const WP_STATUS_OPTS = [
  { value: 'passed',  label: 'Passed' },
  { value: 'failed',  label: 'Exception' },
  { value: 'warning', label: 'Inconclusive' },
];

function WPStatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  if (s === 'passed')  return <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">PASS</span>;
  if (s === 'failed')  return <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-300 px-1.5 py-0.5 rounded">EXCEPTION</span>;
  if (s === 'warning') return <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">INCONCLUSIVE</span>;
  return <span className="text-[10px] text-gray-400">—</span>;
}


/**
 * ATTRIBUTE TESTING TABLE — OPERATING EFFECTIVENESS  (Section IV)
 *
 * Layout (matches manual tester Excel format):
 *   # | Sample Ref | [planField value cols…] | [Attr A / B / C…] | Conclusion
 *
 * planFields  — field definitions from the test plan (label + field_key).
 *               If empty, auto-discovers keys from AI-extracted sr.attributes data.
 * livePlanFields — current live plan fields; used to overlay human-configured labels
 *               over snapshot labels when they differ.
 * attributes  — attribute results, each with sample_results[].{status, attributes}.
 *               sr.attributes is the Dict[str, str] the AI stores (planField key → value).
 * expectedSamples — ordered sample refs from run.samples or evidenceMap.
 * evidenceMap — used to show evidence filename under sample ref.
 * runId       — used to persist column visibility state in localStorage.
 */

export { lookupField };

function AttributeFindingsPanel({ attributes, filterAttr = 'all', runId = null }) {
  const [collapsed, setCollapsed] = useState(false);
  const [overrides, setOverrides] = useState({});

  const visible = attributes.filter(a =>
    (a.findings_summary || a.detailed_findings || a.ai_status || a.overall_status || a.status) &&
    (filterAttr === 'all' || (a.attribute_ref || '?') === filterAttr)
  );

  if (visible.length === 0) return null;

  const statusBorderCls = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'passed')  return 'border-green-400';
    if (s === 'failed')  return 'border-red-400';
    if (s === 'warning') return 'border-amber-400';
    return 'border-slate-300';
  };

  const patchAttr = async (attrId, payload) => {
    const ov = { ...overrides };
    ov[attrId] = { ...(ov[attrId] || {}), ...payload };
    setOverrides(ov);
    try {
      await axiosInstance.patch(
        `/api/control-test-runs/${runId}/attribute-results/${attrId}/human-override`,
        payload,
      );
    } catch (err) {
      toast.error('Failed to save override');
      // Roll back only the fields we tried to save, not other already-saved overrides.
      setOverrides(prev => {
        const entry = { ...(prev[attrId] || {}) };
        Object.keys(payload).forEach(k => { delete entry[k]; });
        const next = { ...prev };
        if (Object.keys(entry).length === 0) delete next[attrId];
        else next[attrId] = entry;
        return next;
      });
      throw err;
    }
  };

  return (
    <div className="border-t border-gray-200">
      {/* Section header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-5 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Attribute Testing Results
          </span>
          <span className="text-[10px] text-slate-400">({visible.length} attribute{visible.length !== 1 ? 's' : ''})</span>
        </div>
        {collapsed
          ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          : <ChevronUp   className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
      </button>

      {!collapsed && (
        <div className="divide-y divide-gray-100">
          {visible.map((attr) => {
            const ref = attr.attribute_ref || '?';
            const ov  = overrides[attr.id] || {};

            const aiStatus      = attributeAiStatus(attr);
            const humanStatus   = attributeHumanStatus(attr, ov);
            const effectiveStatus = attributeDisplayStatus(attr, ov);

            const { ai: aiFinding, human: humanFinding } = attributeFinding(attr, ov);

            const canEdit = !!runId;
            const notCompleted = notCompletedCount(attr);

            return (
              <div key={ref} className={`px-5 py-3 bg-white border-l-4 ${statusBorderCls(effectiveStatus)}`}>
                <div className="flex items-start gap-3">
                  {/* Attribute badge */}
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-200 shrink-0 mt-0.5">
                    {ref}
                  </span>
                  <div className="min-w-0 flex-1 space-y-2">
                    {/* Label + status */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {attr.label && (
                        <span className="text-xs font-semibold text-gray-800">{attr.label}</span>
                      )}
                      <EditableField
                        aiValue={aiStatus}
                        humanValue={humanStatus}
                        fieldType="select"
                        options={WP_STATUS_OPTS}
                        renderValue={(v) => <WPStatusBadge status={v} />}
                        onSave={(val) => patchAttr(attr.id, { human_status: val || null })}
                        onReset={() => patchAttr(attr.id, { human_status: null })}
                        locked={!canEdit}
                        showBadge={true}
                        tooltipAlign="right"
                        emptyLabel="No status"
                      />
                    </div>

                    {notCompleted > 0 && (
                      <p className="text-[11px] text-gray-600">
                        {notCompleted} of {(attr.sample_results || []).length} sample{notCompleted !== 1 ? 's' : ''} not completed (the check timed out or failed) — re-run the test for a full result.
                      </p>
                    )}

                    {/* Finding text — editable */}
                    {(aiFinding || humanFinding) && (
                      <EditableField
                        aiValue={aiFinding}
                        humanValue={humanFinding}
                        fieldType="textarea"
                        placeholder="Enter finding notes…"
                        onSave={(val) => patchAttr(attr.id, { human_detailed_findings: val || null })}
                        onReset={() => patchAttr(attr.id, { human_detailed_findings: null })}
                        locked={!canEdit}
                        showBadge={true}
                        className="w-full"
                      />
                    )}

                    {/* Detailed findings (auditor observations) — static, separate field */}
                    {attr.detailed_findings && !humanFinding && (
                      <div className="mt-0.5 pt-1.5 border-t border-gray-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5 text-gray-400">Observations</p>
                        <p className="text-xs leading-relaxed whitespace-pre-wrap text-gray-700">
                          {attr.detailed_findings}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SampleDataTable({
  attributes      = [],
  planFields      = [],
  livePlanFields  = [],   // current live plan fields — used to overlay human-configured labels
  expectedSamples = [],
  evidenceMap     = [],
  runId           = null, // for localStorage persistence of column state
  onRowClick      = null, // (sampleRef) => void — if provided, rows are clickable
  selectedRef     = null, // normalised ref of the row shown in the inline detail panel
  isLocked        = false,
  onAfterOverride = null, // () => void — called after any sample human-result override saves
  currency        = null, // ISO code of the run's currency (null: amounts shown as plain numbers)
}) {
  // Local override map: sampleId → { humanResult, finalResult, humanRationale }
  const [overrides, setOverrides] = useState({});
  // openCell: { rowRef, attrRef, sampleId, aiResult, humanResult, humanRationale } | null
  const [openCell, setOpenCell] = useState(null);
  const [filterAttr, setFilterAttr] = useState('all');

  // ── Column visibility management ──────────────────────────────────────────
  const [hiddenFieldIds, setHiddenFieldIds] = useState(() => loadColumnChoices(runId).hidden);
  // Fields the AI found beyond the plan are hidden unless the run marked them very relevant; ones the reviewer adds
  // from Columns are remembered here.
  const [shownExtraIds, setShownExtraIds] = useState(() => loadColumnChoices(runId).shown);
  const _persistShown = (ids) => saveColumnChoice(runId, 'shown', ids);
  const [customFieldKeys, setCustomFieldKeys] = useState(() => loadColumnChoices(runId).custom);
  const [showColManager, setShowColManager] = useState(false);
  const [addFieldInput, setAddFieldInput] = useState('');
  const colManagerRef = useRef(null);

  // Close column manager on outside click
  useEffect(() => {
    if (!showColManager) return;
    const handler = (e) => {
      if (colManagerRef.current && !colManagerRef.current.contains(e.target)) {
        setShowColManager(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColManager]);

  const _persistHidden = (ids) => saveColumnChoice(runId, 'hidden', ids);
  const _persistCustom = (keys) => saveColumnChoice(runId, 'custom', keys);

  const hideField = (id) => {
    const updated = hiddenFieldIds.includes(id) ? hiddenFieldIds : [...hiddenFieldIds, id];
    setHiddenFieldIds(updated);
    _persistHidden(updated);
    if (shownExtraIds.includes(id)) {
      const shown = shownExtraIds.filter(x => x !== id);
      setShownExtraIds(shown);
      _persistShown(shown);
    }
  };
  const restoreField = (id) => {
    const updated = hiddenFieldIds.filter(x => x !== id);
    setHiddenFieldIds(updated);
    _persistHidden(updated);
    if (id.startsWith('_aiex_') && !shownExtraIds.includes(id)) {
      const shown = [...shownExtraIds, id];
      setShownExtraIds(shown);
      _persistShown(shown);
    }
  };
  const addCustomField = () => {
    const raw = addFieldInput.trim();
    if (!raw) return;
    // Accept as-is if it looks like a field_key (underscores), else normalize
    const key = raw.replace(/\s+/g, '_').toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!key) return;
    if (!customFieldKeys.includes(key)) {
      const updated = [...customFieldKeys, key];
      setCustomFieldKeys(updated);
      _persistCustom(updated);
    }
    // Restore if it was hidden
    restoreField(`_custom_${key}`);
    setAddFieldInput('');
  };
  const removeCustomField = (key) => {
    const updated = customFieldKeys.filter(k => k !== key);
    setCustomFieldKeys(updated);
    _persistCustom(updated);
    restoreField(`_custom_${key}`);
  };

  /* ── attribute column list ──────────────────────────────────────────────── */
  const attrCols = attributes.map((a) => ({
    id:    a.attribute_id,
    ref:   a.attribute_ref || '?',
    label: a.label || '',
  }));

  const visibleAttrCols = filterAttr === 'all'
    ? attrCols
    : attrCols.filter((c) => c.ref === filterAttr);

  /* ── pivot: one row per sample, each attribute's result and the values found (report/fields.ts) ────── */
  const { rows, aiFieldMeta, fieldSources, reviewNotes } = pivotSamples(attributes, expectedSamples);

  /* ── columns: plan fields (live labels), AI-found fields, the reviewer's choices (report/fields.ts) ── */
  const effectiveFields = planFieldColumns(planFields, livePlanFields, rows, aiFieldMeta);
  const aiExtraFields = aiExtraColumns(planFields, effectiveFields, rows, aiFieldMeta);
  const { allFields, visibleFields, hiddenFields, hiddenExtras, effectiveHiddenIds } = resolveColumns(
    effectiveFields, aiExtraFields, { hidden: hiddenFieldIds, shown: shownExtraIds, custom: customFieldKeys }, aiFieldMeta,
  );
  const hasFields     = allFields.length > 0;

  /* ── helpers ────────────────────────────────────────────────────────────── */
  // Human override (local optimistic state) wins over server data, which wins over AI.
  // Normalize raw DB values to the canonical set cellIcon/rowConclusion expect.
  // Backend stores 'pass' but checks compare against 'passed'; normalize here once.
  const localFinal = (cell) => (cell?.sampleId ? overrides[cell.sampleId]?.finalResult : null);

  const getDisplayStatus = useCallback(
    (cell) => sampleDisplayStatus(cell, localFinal(cell)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overrides],
  );

  const hasHumanOverride = useCallback(
    (cell) => sampleHasReviewerOverride(cell, localFinal(cell)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overrides],
  );

  // What the Result cell of a line needs to know about its sample + attribute: whether the reviewer can override
  // it, and whether they already have.
  const cellMeta = (cell) => {
    const human = hasHumanOverride(cell);
    return {
      canOverride: !isLocked && !!runId && !!cell?.sampleId,
      reviewerLabel: human ? reviewerLabel(getDisplayStatus(cell)) : null,
    };
  };

  const handleCellClick = (e, rowRef, attrRef, cell) => {
    e.stopPropagation();
    if (isLocked || !runId || !cell?.sampleId) return;
    if (openCell?.rowRef === rowRef && openCell?.attrRef === attrRef) {
      setOpenCell(null);
      return;
    }
    const localOvr = overrides[cell.sampleId];
    setOpenCell({
      rowRef, attrRef,
      sampleId:               cell.sampleId,
      aiResult:               cell.aiResult || cell.status,
      humanResult:            localOvr?.finalResult || cell.finalResult || cell.humanResult || '',
      humanRationale:         localOvr?.rationale  || cell.humanRationale || '',
      excluded:               cell.excluded,
      exclusionReason:        cell.exclusionReason,
      needsAdditionalEvidence: cell.needsAdditionalEvidence,
      attrLabel:              visibleAttrCols.find(c => c.ref === attrRef)?.label || attrRef,
    });
  };

  const handleOverrideSave = async (sampleId, humanResult, rationale, extra = {}) => {
    try {
      if (extra.clear_human_review) {
        await axiosInstance.patch(`/api/control-test-runs/${runId}/samples/${sampleId}/human-review`, {
          clear_human_review: true,
        });
        setOverrides(prev => {
          const next = { ...prev };
          delete next[sampleId];
          return next;
        });
        toast.success('Override cleared.');
      } else {
        const body = { human_result: humanResult, rationale, ...extra };
        await axiosInstance.patch(`/api/control-test-runs/${runId}/samples/${sampleId}/human-review`, body);
        setOverrides(prev => ({
          ...prev,
          [sampleId]: { finalResult: humanResult, rationale },
        }));
        toast.success('Override saved.');
      }
      setOpenCell(null);
      onAfterOverride?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Save failed.');
    }
  };

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <div>
      {/* Top bar: AI notice + column manager + attribute filter */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 flex-wrap">
        {hasFields ? (
          <span className="flex items-center gap-1 text-[10px] text-indigo-600">
            <Sparkles className="h-3 w-3 shrink-0" />
            Field values AI-extracted from evidence — verify against source documents.
            {hiddenExtras.length > 0 && (
              <span className="ml-1 text-violet-600">
                · {hiddenExtras.length} more field{hiddenExtras.length !== 1 ? 's' : ''} found by AI — add from Columns.
              </span>
            )}
          </span>
        ) : (
          <span className="text-[10px] text-amber-600">
            No field attributes configured in the Test Plan. Only attribute tick marks shown.
          </span>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {/* Column manager */}
          {hasFields && (
            <div className="relative" ref={colManagerRef}>
              <button
                onClick={() => setShowColManager(p => !p)}
                className={`inline-flex items-center gap-1 text-xs px-2 py-1 border rounded transition-colors ${
                  hiddenFields.length + hiddenExtras.length > 0
                    ? 'border-indigo-300 text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-100'
                }`}
                title="Manage columns"
              >
                <Eye className="h-3 w-3" />
                Columns
                {hiddenFields.length + hiddenExtras.length > 0 && (
                  <span className="inline-flex items-center justify-center h-4 min-w-[1rem] px-0.5 rounded-full bg-indigo-600 text-white text-[9px] font-bold ml-0.5">
                    {hiddenFields.length + hiddenExtras.length}
                  </span>
                )}
              </button>

              {showColManager && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 text-left">
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                    <p className="text-xs font-semibold text-gray-700">Field Columns</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {visibleFields.length} visible · {hiddenFields.length + hiddenExtras.length} hidden
                    </p>
                  </div>

                  {/* Fields the AI found beyond the plan, most relevant first */}
                  {hiddenExtras.length > 0 && (
                    <div className="px-3 py-2 border-b border-gray-100 max-h-56 overflow-y-auto">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Found by AI — not in the plan</p>
                      <div className="flex flex-wrap gap-1.5">
                        {hiddenExtras.map(f => (
                          <button
                            key={f.id}
                            onClick={() => restoreField(f.id)}
                            title={`Add as a column (found on ${f._samples} of ${rows.length} samples)`}
                            className="inline-flex items-center gap-1 text-[10px] border border-dashed border-violet-200 bg-violet-50/40 text-gray-600 px-2 py-0.5 rounded hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors"
                          >
                            <Plus className="h-2.5 w-2.5" />
                            {f.label}
                            <span className="text-gray-400 ml-0.5">{f._samples}/{rows.length}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hidden fields to restore */}
                  {hiddenFields.length > 0 && (
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Hidden</p>
                      <div className="flex flex-wrap gap-1.5">
                        {hiddenFields.map(f => (
                          <button
                            key={f.id}
                            onClick={() => restoreField(f.id)}
                            title="Click to restore"
                            className="inline-flex items-center gap-1 text-[10px] border border-dashed border-gray-300 bg-gray-50 text-gray-500 px-2 py-0.5 rounded hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors"
                          >
                            <Plus className="h-2.5 w-2.5" />
                            {f._liveLabel || f.label}
                            {f._aiExtra && <span className="text-violet-400 ml-0.5">AI</span>}
                            {f._customField && <span className="text-orange-400 ml-0.5">custom</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add custom field */}
                  <div className="px-3 py-2.5">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Add Field</p>
                    <p className="text-[10px] text-gray-400 mb-2 leading-snug">
                      Enter a field key or label — e.g. <code className="bg-gray-100 px-0.5 rounded">invoice_amount</code>.
                      Values are looked up from AI-extracted data.
                    </p>
                    <div className="flex gap-1.5">
                      <input
                        type="text"
                        value={addFieldInput}
                        onChange={e => setAddFieldInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomField(); } }}
                        placeholder="e.g. invoice_amount"
                        className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400 min-w-0"
                      />
                      <button
                        onClick={addCustomField}
                        disabled={!addFieldInput.trim()}
                        className="inline-flex items-center gap-1 text-xs font-medium bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 disabled:opacity-40"
                      >
                        <Plus className="h-3 w-3" />Add
                      </button>
                    </div>
                  </div>

                  {/* Remove custom fields */}
                  {customFieldKeys.length > 0 && (
                    <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Custom Fields</p>
                      <div className="flex flex-wrap gap-1.5">
                        {customFieldKeys.map(k => (
                          <span
                            key={k}
                            className="inline-flex items-center gap-1 text-[10px] border border-orange-200 bg-orange-50 text-orange-700 px-2 py-0.5 rounded"
                          >
                            {toTitle(k)}
                            <button
                              onClick={() => removeCustomField(k)}
                              className="hover:text-red-600 ml-0.5"
                              title="Remove custom field"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Attribute filter */}
          <span className="text-xs text-gray-500">Show:</span>
          <div className="relative">
            <select
              value={filterAttr}
              onChange={(e) => setFilterAttr(e.target.value)}
              className="appearance-none text-xs bg-white border border-gray-300 rounded px-2 py-1 pr-6 text-gray-700 focus:outline-none cursor-pointer"
            >
              <option value="all">All attributes</option>
              {attrCols.map((c) => (
                <option key={c.ref} value={c.ref}>
                  {c.ref}{c.label ? ` — ${c.label}` : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400 italic">
          No samples found for this run.
        </div>
      ) : (
        <>
        {reviewNotes.length > 0 && (
          <div className="mx-4 mt-3 mb-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="review-notes">
            <div className="font-semibold mb-1">Reviewer to check <span className="font-normal text-amber-700">(not counted as line items; use the result chip to flag an exception if required)</span></div>
            <ul className="space-y-0.5">
              {reviewNotes.map((n) => (
                <li key={`${n.sample}-${n.attrRef}`}><span className="font-semibold">{n.attrRef}</span>{n.sample ? ` · ${n.sample}` : ''}: {n.note}</li>
              ))}
            </ul>
          </div>
        )}
        <LineItemTable
          rows={rows}
          attrCols={visibleAttrCols}
          evidenceMap={evidenceMap}
          sampleFields={allFields}
          hiddenFieldIds={effectiveHiddenIds}
          fieldSources={fieldSources}
          currency={currency}
          onHideSampleField={hideField}
          lookupField={lookupField}
          runId={runId}
          cellMeta={cellMeta}
          onResultClick={handleCellClick}
          openCell={openCell}
        />
        </>
      )}

      {/* Per-cell override bar — appears when a reviewer clicks an attribute cell */}
      {openCell && (
        <SampleOverrideBar
          openCell={openCell}
          onSave={handleOverrideSave}
          onClose={() => setOpenCell(null)}
        />
      )}

      {/* Attribute Testing Results — findings per attribute, same data as Work Performed section */}
      {attributes.some(a => a.findings_summary || a.detailed_findings) && (
        <AttributeFindingsPanel attributes={attributes} filterAttr={filterAttr} runId={runId} />
      )}

    </div>
  );
}

/* ── SampleOverrideBar ─────────────────────────────────────────────────────── */

const OVERRIDE_OPTIONS = [
  { value: 'pass',      label: 'Pass',        cls: 'border-green-500 bg-green-50 text-green-700',  Icon: Check },
  { value: 'exception', label: 'Exception',   cls: 'border-red-500 bg-red-50 text-red-700',        Icon: X },
  { value: 'n_a',       label: 'N/A',         cls: 'border-gray-400 bg-gray-50 text-gray-600',     Icon: Minus },
];

export function SampleOverrideBar({ openCell, onSave, onClose }) {
  const [result, setResult]       = useState(openCell.humanResult || '');
  const [rationale, setRationale] = useState(openCell.humanRationale || '');
  const [excluded, setExcluded]   = useState(!!openCell.excluded);
  const [excludeReason, setExcludeReason] = useState(openCell.exclusionReason || '');
  const [needsEvidence, setNeedsEvidence] = useState(!!openCell.needsAdditionalEvidence);
  const [saving, setSaving]       = useState(false);
  const [clearing, setClearing]   = useState(false);
  const hasExistingOverride = !!(openCell.humanResult);

  const aiLabel = (s) => {
    if (!s) return '—';
    if (s === 'passed' || s === 'pass') return 'PASS';
    if (s === 'exception' || s === 'failed') return 'EXCEPTION';
    if (s === 'n_a') return 'N/A';
    return s.toUpperCase();
  };

  const handleSave = async () => {
    if (!result) { toast.error('Select a result first.'); return; }
    setSaving(true);
    const extra = {};
    if (excluded !== !!openCell.excluded) {
      extra.excluded = excluded;
      extra.exclusion_reason = excludeReason || null;
    }
    if (needsEvidence !== !!openCell.needsAdditionalEvidence) {
      extra.needs_additional_evidence = needsEvidence;
    }
    await onSave(openCell.sampleId, result, rationale, extra);
    setSaving(false);
  };

  const handleClear = async () => {
    setClearing(true);
    await onSave(openCell.sampleId, null, '', { clear_human_review: true });
    setClearing(false);
  };

  return (
    <div className="border-t border-indigo-200 bg-indigo-50/60 px-4 py-3">
      <div className="flex items-start gap-4 flex-wrap">
        {/* Context */}
        <div className="min-w-[160px]">
          <p className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">Override</p>
          <p className="text-xs font-mono font-semibold text-gray-800 mt-0.5">{openCell.rowRef}</p>
          <p className="text-[10px] text-gray-500">Attr {openCell.attrRef}{openCell.attrLabel && openCell.attrLabel !== openCell.attrRef ? ` — ${openCell.attrLabel}` : ''}</p>
          <p className="text-[10px] text-gray-400 mt-1">
            AI: <span className="font-semibold text-blue-600">{aiLabel(openCell.aiResult)}</span>
          </p>
        </div>

        {/* Result buttons */}
        <div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Result</p>
          <div className="flex gap-2">
            {OVERRIDE_OPTIONS.map(({ value, label, cls, Icon }) => (
              <button
                key={value}
                onClick={() => setResult(result === value ? '' : value)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-semibold transition-colors ${
                  result === value ? cls : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <Icon className="h-3 w-3" />{label}
              </button>
            ))}
          </div>
        </div>

        {/* Rationale */}
        <div className="flex-1 min-w-[180px]">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Rationale</p>
          <textarea
            rows={2}
            value={rationale}
            onChange={e => setRationale(e.target.value)}
            placeholder="Basis for override (optional)"
            className="w-full text-xs border border-indigo-200 rounded px-2.5 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
          />
        </div>

        {/* Exclusion + needs-evidence toggles */}
        <div className="flex flex-col gap-2 min-w-[160px]">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Flags</p>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={excluded} onChange={e => setExcluded(e.target.checked)}
              className="h-3 w-3 rounded border-gray-300 text-red-500 focus:ring-red-400" />
            <span className="text-[10px] text-gray-600">Exclude from population</span>
          </label>
          {excluded && (
            <input
              value={excludeReason}
              onChange={e => setExcludeReason(e.target.value)}
              placeholder="Exclusion reason (required)"
              className="text-xs border border-red-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-red-300"
            />
          )}
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={needsEvidence} onChange={e => setNeedsEvidence(e.target.checked)}
              className="h-3 w-3 rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
            <span className="text-[10px] text-gray-600">Needs additional evidence</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 justify-center pt-4">
          <button
            onClick={handleSave}
            disabled={saving || !result}
            className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}Save
          </button>
          {hasExistingOverride && (
            <button
              onClick={handleClear}
              disabled={clearing || saving}
              className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 disabled:opacity-40 inline-flex items-center gap-1"
            >
              {clearing && <Loader2 className="h-3 w-3 animate-spin" />}Clear Override
            </button>
          )}
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
